import React, { useCallback, useEffect, useState, useRef } from 'react';
import { BottomSection, ButtonSection, Label, Logo, MainCard, Title, TopRightSection, TopSection } from './styles';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE, DEPLOYMENT_STATUS_LABEL, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../global/constants';
import { createBranchDeployment, getVercelDeployment } from '../../services/vercelService';
import { dispatchTaskError, dispatchTaskSuccess } from '../../services/taskServices';
import { VercelDeployments } from '../../mobx/interfaces/project';
import { isEmpty } from '../../helpers/common';
import Button from '../button';
import IconButton from '../button/iconButton';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import Image from '../image';

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(duration);

type DeployingBranchesState = Record<string, boolean>;

const VercelCard = () => {
  //Mobx store variables
  const { taskStore, projectStore } = rootStore;
  const { currentTask } = taskStore;
  const { currentProject } = projectStore;

  // Add state for polling and timer
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [deployingBranches, setDeployingBranches] = useState<DeployingBranchesState>({});

  //Handle preview link visit click
  const onClickPreviewLink = useCallback((url: string) => {
    window.open(`https://${url}`, '_blank');
  }, []);

  // Function to update elapsed time
  const updateElapsedTime = useCallback((deploy: { createdAt: number }) => {
    const startTime = dayjs(deploy.createdAt);
    const now = dayjs();
    const durationValue = dayjs.duration(now.diff(startTime));
    const minutes = String(Math.floor(durationValue.asMinutes())).padStart(2, '0');
    const seconds = String(durationValue.seconds()).padStart(2, '0');
    return `${minutes}:${seconds}`;
  }, []);

  const loadData = useCallback(async () => {
    if (currentProject?.id && currentTask?.id) {
      await getVercelDeployment(currentProject.id, currentTask.id);
    }
  }, [currentProject?.id, currentTask?.id]);

  // Polling effect for running deployments
  useEffect(() => {
    // Check if any deployment is running
    const isRunning =
      currentTask?.vercelDeployments &&
      currentTask.vercelDeployments.length > 0 &&
      currentTask.vercelDeployments.some(
        (deploy) =>
          deploy.state === DEPLOYMENT_STATUS_LABEL.BUILDING ||
          deploy.state === DEPLOYMENT_STATUS_LABEL.INITIALIZING ||
          deploy.state === DEPLOYMENT_STATUS_LABEL.QUEUED
      );

    if (isRunning) {
      // Start polling every 10 seconds
      intervalRef.current = setInterval(() => {
        loadData();
      }, 10000);

      // Update elapsed time every second
      const timeInterval = setInterval(() => {
        const runningDeploy = currentTask?.vercelDeployments?.find(
          (deploy) =>
            deploy.state === DEPLOYMENT_STATUS_LABEL.BUILDING ||
            deploy.state === DEPLOYMENT_STATUS_LABEL.INITIALIZING ||
            deploy.state === DEPLOYMENT_STATUS_LABEL.QUEUED
        );

        if (runningDeploy) {
          setElapsedTime(updateElapsedTime(runningDeploy));
        }
      }, 1000);

      // Cleanup function
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        clearInterval(timeInterval);
      };
    } else {
      // Clear intervals if no running deployment
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setElapsedTime('');
    }
  }, [loadData, currentTask?.vercelDeployments, updateElapsedTime]);

  const onClickDeploy = useCallback(
    async (branchName: string) => {
      setDeployingBranches((previous: DeployingBranchesState) => ({ ...previous, [branchName]: true }));

      try {
        const payload = {
          branchName,
          projectId: currentProject?.id
        };
        const response = await createBranchDeployment(payload);

        if (response?.data?.id) {
          dispatchTaskSuccess('Deployment added in queue successfully');

          await getVercelDeployment(currentProject?.id, currentTask?.id);
          setDeployingBranches((previous: DeployingBranchesState) => ({ ...previous, [branchName]: false }));
        } else {
          dispatchTaskError('Something went wrong! Deployment not done');

          setDeployingBranches((previous: DeployingBranchesState) => ({ ...previous, [branchName]: false }));
        }
      } catch (error) {
        console.log('Error', error);
        setDeployingBranches((previous: DeployingBranchesState) => ({ ...previous, [branchName]: false }));
      }
    },
    [currentProject?.id, currentTask?.id]
  );

  // Renders the main Vercel deployment card UI for a given deployment state
  const vercelMainCard = useCallback(
    (isEmptyState?: boolean, deploy?: VercelDeployments, isRunning?: boolean) => {
      const latestPrBranch =
        isEmptyState && currentTask?.pullRequests && currentTask.pullRequests.length > 0
          ? currentTask.pullRequests[currentTask.pullRequests.length - 1].branchName || ''
          : '';
      const branchName = !isEmptyState && deploy ? deploy?.branchName : latestPrBranch;

      const isApiCallInFlight: boolean = Boolean(deployingBranches[branchName]);
      const shouldDisableButton: boolean = Boolean(isRunning) || isApiCallInFlight;

      return (
        <>
          <MainCard key={deploy?.id}>
            <TopSection>
              <TopRightSection>
                <Logo>
                  <Image imageKey='vercel_logo' alt='vercel-logo' width={20} height={20} />
                </Logo>
                <Label>Vercel</Label>
              </TopRightSection>
              <ButtonSection>
                {!isEmptyState && deploy?.url && (
                  <IconButton
                    iconName='arrow-ne'
                    size={ICON_BUTTON_SIZE.MEDIUM}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    onClick={() => onClickPreviewLink(deploy?.url)}
                  />
                )}
                <Button
                  title={isEmptyState ? 'Deploy' : 'Redeploy'}
                  variant={BUTTON_TYPE.SECONDARY}
                  size='small'
                  disabled={shouldDisableButton}
                  onClick={() => onClickDeploy(branchName)}
                />
              </ButtonSection>
            </TopSection>
            <BottomSection>
              {!isEmptyState && deploy?.ready && <Title>Last Deployed: {dayjs(deploy?.ready).fromNow()}</Title>}
              {(deploy?.aheadCommits || 0) > 0 && <Title>⚠️ GitHub ahead by {deploy?.aheadCommits} commits</Title>}
              {deploy?.state === DEPLOYMENT_STATUS_LABEL.CANCELED && <Title>Deployment cancelled</Title>}
              {deploy?.state === DEPLOYMENT_STATUS_LABEL.ERROR && <Title>🧾️ Last deployment was failed</Title>}
              {isRunning && <Title>Deployment is running... {elapsedTime}</Title>}
              {isEmptyState && <Title>No Deployment yet</Title>}
            </BottomSection>
          </MainCard>
        </>
      );
    },
    [currentTask?.pullRequests, elapsedTime, onClickDeploy, onClickPreviewLink, deployingBranches]
  );
  return (
    <>
      {/* show empty state */}
      {currentTask?.pullRequests &&
        currentTask?.pullRequests?.length > 0 &&
        (!currentTask?.vercelDeployments || isEmpty(currentTask?.vercelDeployments)) &&
        vercelMainCard(true)}

      {/* Map all the vercel deployments */}
      {currentTask?.pullRequests &&
        currentTask?.pullRequests?.length > 0 &&
        currentTask?.vercelDeployments &&
        currentTask?.vercelDeployments.length > 0 &&
        currentTask?.vercelDeployments.map((deploy) => {
          const isRunning =
            deploy.state === DEPLOYMENT_STATUS_LABEL.BUILDING ||
            deploy.state === DEPLOYMENT_STATUS_LABEL.INITIALIZING ||
            deploy.state === DEPLOYMENT_STATUS_LABEL.QUEUED;
          return vercelMainCard(false, deploy, isRunning);
        })}
    </>
  );
};

export default observer(VercelCard);
